import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useOnboardingStatus } from "@/hooks/use-onboarding-status";
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  FileText,
  Stethoscope,
  ClipboardCheck,
  Activity,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface OnboardingProgressCardProps {
  clientId: string;
}

interface ChecklistItemProps {
  label: string;
  description?: string;
  completed: boolean;
  hasIssue?: boolean;
  icon: React.ElementType;
}

function ChecklistItem({
  label,
  description,
  completed,
  hasIssue = false,
  icon: Icon,
}: ChecklistItemProps) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
      <div
        className={cn(
          "p-1.5 rounded-full",
          completed && !hasIssue && "bg-success/15",
          hasIssue && "bg-destructive/15",
          !completed && !hasIssue && "bg-muted/15"
        )}
      >
        {completed && !hasIssue ? (
          <CheckCircle2 className="h-4 w-4 text-success" />
        ) : hasIssue ? (
          <AlertTriangle className="h-4 w-4 text-destructive" />
        ) : (
          <Circle className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className={cn("font-medium text-sm", completed && "text-muted-foreground")}>
            {label}
          </span>
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}

export function OnboardingProgressCard({ clientId }: OnboardingProgressCardProps) {
  const { status, loading, error } = useOnboardingStatus(clientId);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error || !status) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {error || "Unable to load onboarding status"}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Onboarding Progress
            </CardTitle>
            <CardDescription>
              {status.isComplete
                ? "All onboarding requirements completed"
                : `${status.completionPercentage}% complete`}
            </CardDescription>
          </div>
          {status.isComplete ? (
            <Badge className="bg-success/15 text-success hover:bg-success/15">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Complete
            </Badge>
          ) : status.clearanceRequired ? (
            <Badge variant="destructive">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Action Required
            </Badge>
          ) : (
            <Badge variant="secondary">In Progress</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={status.completionPercentage} className="h-2" />
          <p className="text-xs text-muted-foreground text-right">
            {status.completionPercentage}% complete
          </p>
        </div>

        {/* Checklist */}
        <div className="space-y-1">
          {/* PAR-Q */}
          <ChecklistItem
            label="PAR-Q Questionnaire"
            description={
              status.hasCompletedParq
                ? status.parqHasFlags
                  ? "Completed - flags detected"
                  : "Completed - no concerns"
                : "Physical Activity Readiness Questionnaire"
            }
            completed={status.hasCompletedParq}
            hasIssue={status.parqHasFlags && !status.hasActiveClearance}
            icon={ClipboardCheck}
          />

          {/* Medical Clearance (only show if PAR-Q has flags) */}
          {status.parqHasFlags && (
            <ChecklistItem
              label="Medical Clearance"
              description={
                status.hasActiveClearance
                  ? "Clearance on file"
                  : "Required due to PAR-Q responses"
              }
              completed={status.hasActiveClearance}
              hasIssue={!status.hasActiveClearance}
              icon={Stethoscope}
            />
          )}

          {/* Waivers */}
          <ChecklistItem
            label="Required Waivers"
            description={
              status.requiredWaiversCount === 0
                ? "No required waivers"
                : `${status.validWaiversCount}/${status.requiredWaiversCount} signed`
            }
            completed={status.hasAllRequiredWaivers}
            icon={FileText}
          />

          {/* Intake Forms */}
          {status.intakeFormsTotal > 0 && (
            <ChecklistItem
              label="Intake Forms"
              description={`${status.intakeFormsCompleted}/${status.intakeFormsTotal} completed`}
              completed={status.intakeFormsCompleted >= status.intakeFormsTotal}
              icon={FileText}
            />
          )}

          {/* Movement Assessment */}
          <ChecklistItem
            label="Movement Assessment"
            description={
              status.hasCompletedAssessment
                ? `Score: ${status.latestAssessmentScore}/${status.latestAssessmentMaxScore}`
                : "Optional - FMS or similar screening"
            }
            completed={status.hasCompletedAssessment}
            icon={Activity}
          />
        </div>
      </CardContent>
    </Card>
  );
}
